import React, { useState, useEffect, useCallback } from 'react';
import licenseService from '../../services/licenseService';
import { Clock, CheckCircle, XCircle, RotateCcw, Download, Loader2, AlertCircle, RefreshCw } from 'lucide-react';

export default function GrantHistory({ customer }) {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [revoking, setRevoking] = useState(null);

  const loadGrants = useCallback(async () => {
    if (!customer?.customer_proxy_team_id) return;

    setLoading(true);
    setError(null);

    try {
      const filters = filter !== 'all' ? { status: filter } : {};
      const response = await licenseService.listGrants(customer.customer_proxy_team_id, filters);
      if (response.success) {
        setGrants(response.grants || []);
      } else {
        setError(response.error || 'Failed to load grants');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [customer?.customer_proxy_team_id, filter]);

  useEffect(() => {
    loadGrants();
  }, [loadGrants]);

  const handleRevoke = async (grantId) => {
    if (!window.confirm('Are you sure you want to revoke this grant? This cannot be undone.')) {
      return;
    }

    setRevoking(grantId);
    try {
      await licenseService.revokeGrant(grantId);
      await loadGrants();
    } catch (err) {
      console.error('Failed to revoke grant:', err);
      alert(`Failed to revoke: ${err.message}`);
    } finally {
      setRevoking(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'expired':
        return <Clock className="w-4 h-4 text-gray-500" />;
      case 'revoked':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'superseded':
        return <RotateCcw className="w-4 h-4 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-900/30 text-green-400 border-green-700';
      case 'expired':
        return 'bg-gray-900/30 text-gray-400 border-gray-700';
      case 'revoked':
        return 'bg-red-900/30 text-red-400 border-red-700';
      case 'superseded':
        return 'bg-yellow-900/30 text-yellow-400 border-yellow-700';
      default:
        return 'bg-gray-900/30 text-gray-400 border-gray-700';
    }
  };

  const parseEntitlements = (entitlementsJson) => {
    try {
      if (typeof entitlementsJson === 'string') {
        return JSON.parse(entitlementsJson);
      }
      return entitlementsJson;
    } catch {
      return {};
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-400">Loading grants...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500 rounded-lg p-4 flex items-center">
        <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
        <span>{error}</span>
        <button
          onClick={loadGrants}
          className="ml-auto px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          {['all', 'active', 'expired', 'superseded', 'revoked'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                filter === status
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
        <button
          onClick={loadGrants}
          className="p-2 text-gray-400 hover:text-white transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {grants.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No grants found{filter !== 'all' ? ` with status "${filter}"` : ''}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {grants.map(grant => {
            const entitlements = parseEntitlements(grant.entitlements_json);
            const isExpiringSoon = grant.status === 'active' &&
              new Date(grant.valid_until) <= new Date(Date.now() + 60 * 24 * 60 * 60 * 1000);

            return (
              <div
                key={grant.id}
                className={`bg-gray-700/50 rounded-lg p-4 border ${
                  isExpiringSoon ? 'border-yellow-600' : 'border-gray-600'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${getStatusClass(grant.status)}`}>
                        {getStatusIcon(grant.status)}
                        {grant.status}
                      </span>
                      <span className="text-gray-400 text-sm">
                        {grant.product_name || grant.product_id}
                      </span>
                      {isExpiringSoon && (
                        <span className="px-2 py-1 bg-yellow-900/30 text-yellow-400 text-xs rounded">
                          Expiring Soon
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-400">Grant ID:</span>
                        <p className="font-mono text-xs truncate" title={grant.id}>
                          {grant.id}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-400">Seats:</span>
                        <p>{entitlements.max_active_keys || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-gray-400">Valid From:</span>
                        <p>{new Date(grant.valid_from).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <span className="text-gray-400">Valid Until:</span>
                        <p>{new Date(grant.valid_until).toLocaleDateString()}</p>
                      </div>
                    </div>

                    {grant.notes && (
                      <p className="mt-2 text-sm text-gray-400">
                        <span className="font-medium">Notes:</span> {grant.notes}
                      </p>
                    )}

                    <p className="mt-2 text-xs text-gray-500">
                      Issued {new Date(grant.created_at).toLocaleString()}
                      {grant.issued_by_email && ` by ${grant.issued_by_email}`}
                    </p>
                  </div>

                  <div className="flex gap-2 ml-4">
                    {grant.signed_blob && (
                      <button
                        onClick={() => licenseService.downloadGrantFile(
                          grant.signed_blob,
                          `license-${grant.product_id}-${grant.id.slice(-8)}.rigrlic`
                        )}
                        className="p-2 text-gray-400 hover:text-blue-400 transition-colors"
                        title="Download .rigrlic file"
                      >
                        <Download className="w-5 h-5" />
                      </button>
                    )}
                    {grant.status === 'active' && (
                      <button
                        onClick={() => handleRevoke(grant.id)}
                        disabled={revoking === grant.id}
                        className="p-2 text-gray-400 hover:text-red-400 transition-colors disabled:opacity-50"
                        title="Revoke grant"
                      >
                        {revoking === grant.id ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <XCircle className="w-5 h-5" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
